import { Injectable } from '@angular/core';
import { bcEnums } from 'common/common/constants/boldchat-enums';
import { BcRestApi } from 'common/api/rest-api';
import { BcAnalytics } from 'common/analytics/analytics.service';
import { DynamicQueryDecoratorService } from 'common/dynamicquery/dynamic-query-decorator.service';
import { DynamicQueryAdapterService } from 'common/dynamicquery/dynamic-query-adapter.service';
import { DynamicQueryStoreObservableFactory } from 'common/dynamicquery/dynamic-query-store-observable';
import { LogoutSubscriber } from '../subscribers/logout-subscriber.service';

@Injectable()
export class OperatorStatusService {
  public statusChangeInProgress;
  private _serviceStatusMap;

  constructor(private bcRestApi: BcRestApi,
              private bcAnalytics: BcAnalytics,
              private DynamicQuery: DynamicQueryAdapterService,
              private DynamicQueryDecorator: DynamicQueryDecoratorService,
              private DynamicQueryStoreObservableFactory: DynamicQueryStoreObservableFactory,
              private _logoutSubscriber: LogoutSubscriber) {
    this._resetState();
    this._logoutSubscriber.subscribe(() => this._resetState());
  }

  private _resetState() {
    this.statusChangeInProgress = false;
    this._serviceStatusMap = this.getInitialServiceStatuses();
  }

  public setServiceStatusMap(entry) {
    this._serviceStatusMap[entry.ServiceTypeID] = {
      statusType: entry.StatusType,
      customStatusId: entry.CustomOperatorStatusID,
      customStatusName: entry.CustomOperatorStatus,
      operatorCanChange: !entry.CustomOperatorStatusID || entry.OperatorCanChangeStatus === 1
    };
  }

  changeStatusToAvailable(serviceType) {
    this.lockRequest(() => {
      this.bcAnalytics.eventTrack('ChangeStatus', {
        service: this.getServiceKey(serviceType),
        status: 'available'
      });
      return this.bcRestApi.setAvailability(serviceType, true);
    });
  }

  changeStatusToAway(serviceType) {
    this.lockRequest(() => {
      this.bcAnalytics.eventTrack('ChangeStatus', {
        service: this.getServiceKey(serviceType),
        status: 'away'
      });
      return this.bcRestApi.setAvailability(serviceType, false);
    });
  }

  setCustomStatus(customStatusId, serviceType) {
    this.lockRequest(() => {
      this.bcAnalytics.eventTrack('ChangeStatusToCustom', {
        service: this.getServiceKey(serviceType)
      });
      return this.bcRestApi.setCustomStatus(customStatusId, serviceType);
    });
  }

  getServiceKey(serviceType) {
    return bcEnums.serviceNames[serviceType];
  }

  getStatusType(serviceType) {
    return this._serviceStatusMap[serviceType + ''].statusType;
  }

  getStatusKey(serviceType) {
    const statusType = this.getStatusType(serviceType);
    switch (statusType) {
      case bcEnums.statuses.AVAILABLE:
        return 'available';
      case bcEnums.statuses.AWAY:
        return 'away';
      default:
        return 'offline';
    }
  }

  isCustomStatus(serviceType) {
    return !!this._serviceStatusMap[serviceType + ''].customStatusId;
  }

  isLockedStatus(serviceType) {
    return this._serviceStatusMap[serviceType + ''].operatorCanChange === false;
  }

  getCustomStatusName(serviceType, smsDisabled) {
    if (serviceType === bcEnums.services.SMS && smsDisabled) {
      return 'bcOpStatus.smsDisabled';
    }

    return this._serviceStatusMap[serviceType + ''].customStatusName ? this._serviceStatusMap[serviceType + ''].customStatusName : '';
  }

  getAllAvailabilities() {
    return this.bcRestApi.getAllAvailabilities();
  }

  observeClientStatuses() {
    const queryObject = this.DynamicQuery.getQuery('clientServicesByClientID', null);
    const decoratedObject = this.DynamicQueryDecorator.create(queryObject, (event) => {
      return event.data;
    });
    return this.DynamicQueryStoreObservableFactory.create(decoratedObject, (clientService1, clientService2) => {
      return clientService1.ClientServiceID === clientService2.ClientServiceID;
    });
  }

  getInitialServiceStatuses() {
    return Object.keys(bcEnums.services).reduce((map, serviceName) => {
      map[bcEnums.services[serviceName] + ''] = {
        statusType: bcEnums.statuses.OFFLINE
      };
      return map;
    }, {});
  }

  lockRequest(requestBuilder) {
    if (this.statusChangeInProgress) {
      return;
    }
    this.statusChangeInProgress = true;
    return requestBuilder()
      .then(() => this.unlockRequest())
      .catch(() => this.unlockRequest());
  }

  unlockRequest() {
    setTimeout(() => {
      this.statusChangeInProgress = false;
    }, 500);
  }
}
